import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { DoNotTrackCheckbox } from '@/components/content/DoNotTrackCheckbox';
import { LegalHeading } from '@/components/legal/LegalHeading';
import { LegalText } from '@/components/legal/LegalText';
import { ObfuscatedEmail } from '@/components/legal/ObfuscatedEmail';
import { makeMetaTitle, META_ROBOTS_NOINDEX } from '@/modules/META';

export const Route = createFileRoute('/_content/datenschutz')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  head: () => ({
    meta: [makeMetaTitle(['Datenschutz']), META_ROBOTS_NOINDEX],
  }),
});

function RouteComponent() {
  return (
    <Fragment>
      <LegalHeading level="h1" sx={{ marginTop: 0 }}>
        Datenschutzerklärung
      </LegalHeading>

      <LegalText>Information nach Art. 13 bzw. 14 Datenschutz-Grundverordnung (DS-GVO)</LegalText>
      <LegalText>
        Das Ministerium für Verkehr Baden-Württemberg verarbeitet im Rahmen der Bereitstellung des RadMELDER
        Baden-Württemberg personenbezogene Daten.
      </LegalText>
      <LegalText>
        Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Mit diesen Hinweisen informieren wir Sie darüber, zu
        welchen Zwecken und auf welcher Rechtsgrundlage wir Ihre personenbezogenen Daten verarbeiten, an wen Sie sich in
        datenschutzrechtlichen Fragen wenden können und welche Rechte Sie nach der DS-GVO haben.
      </LegalText>
      <LegalText component="ol">
        <li>
          <LegalText fontWeight="bold">Wer ist verantwortlich für die Datenverarbeitung?</LegalText>

          <LegalText>
            Ministerium für Verkehr Baden-Württemberg
            <br />
            Dorotheenstraße 8<br />
            70173 Stuttgart
            <br />
            Telefon: 0711 89686-0
            <br />
            E-Mail: <ObfuscatedEmail name="poststelle" />
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">
            Wie erreichen Sie unsere Datenschutzbeauftragte bzw. unseren Datenschutzbeauftragten?
          </LegalText>

          <LegalText>
            Unsere Datenschutzbeauftragte bzw. unseren Datenschutzbeauftragten erreichen Sie postalisch über die unter
            Ziff. 1 angegebene Postanschrift oder unter folgenden E-Mail-Adressen und Telefonnummern:
          </LegalText>
          <LegalText>
            E-Mail: <ObfuscatedEmail name="datenschutz" />
            <br />
            Telefon: 0711 89686-0
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">
            Was sind der Zweck und die Rechtsgrundlagen der Verarbeitung der personenbezogenen Daten?
          </LegalText>

          <LegalText component="ul">
            <li>
              <LegalText fontWeight="bold">Zweck:</LegalText>

              <LegalText>
                Die Verarbeitung erfolgt zum Zweck der Bereitstellung, Weiterentwicklung, Optimierung und Fehleranalyse
                der Webanwendung RadMELDER Baden-Württemberg sowie zur Bearbeitung eingehender Mängelmeldungen.
              </LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Rechtsgrundlagen:</LegalText>

              <LegalText>
                Die Verarbeitung erfolgt auf der Grundlage von Art. 6 Abs. 1 Buchstabe e) DS-GVO, § 4
                Landesdatenschutzgesetz (LDSG) sowie § 53a Straßengesetz (StrG BW) sowie Art. 6 Abs. 1 Buchstabe a)
                DS-GVO (Einwilligung für optionale Angaben).
              </LegalText>
            </li>
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Welche personenbezogenen Daten werden verarbeitet?</LegalText>
          <LegalText>
            Wir erheben, verarbeiten und nutzen die Daten, die Sie uns im Rahmen der Anwendung und Nutzung des
            RadMELDERs Baden-Württemberg zur Verfügung stellen.
          </LegalText>
          <LegalText>
            Wenn Sie unsere Webseiten besuchen, werden auf unserem Webserver temporär sogenannte Nutzungsdaten zu
            statistischen Zwecken als Protokoll gespeichert, um die Qualität unserer Webseiten zu verbessern. Dazu
            gehören insbesondere:
          </LegalText>
          <LegalText component="ul">
            <li>Name und Adresse der angeforderten Inhalte</li>
            <li>Datum und Uhrzeit der Abfrage</li>
            <li>Übertragene Datenmenge</li>
            <li>Zugriffsstatus (Inhalt übertragen, Inhalt nicht gefunden)</li>
            <li>Beschreibung des verwendeten Webbrowsers und des Betriebssystems</li>
            <li>Referral-Link, der angibt, von welcher Seite Sie auf unsere gelangt sind</li>
            <li>
              IP-Adresse des anfragenden Rechners, die so verkürzt wird, dass ein Personenbezug nicht mehr herstellbar
              ist
            </li>
          </LegalText>
          <LegalText>Zusätzlich können Sie im Rahmen der Meldung von Mängeln folgende Daten angeben:</LegalText>
          <LegalText component="ul">
            <li>
              Name (optional)
              <br />
              Die Angabe von Namen und E-Mail-Adresse erfolgt freiwillig. Ohne diese Angaben kann jedoch keine
              persönliche Rückmeldung durch die zuständige Stelle erfolgen.
            </li>
            <li>E-Mail-Adresse (optional, zur Rückmeldung)</li>
            <li>Standortdaten (Geokoordinaten der Meldung)</li>
            <li>
              Hochgeladene Fotos zur Dokumentation von Mängeln, die Metadaten enthalten können (z. B. Aufnahmeort,
              Datum, technische Daten)
            </li>
            <li>Beschreibung des Mangels (Freitext)</li>
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Woher stammen Ihre Daten?</LegalText>

          <LegalText>
            Wir verwenden ausschließlich Daten, die Sie uns im Rahmen der Nutzung des RadMELDER Baden-Württemberg zur
            Verfügung stellen.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Wie verarbeiten wir diese Daten?</LegalText>

          <LegalText component="ul">
            <li>
              <LegalText fontWeight="bold">Temporäre Nutzerdaten:</LegalText>
              <LegalText>
                Beim Aufruf dieser Internetseite werden die über Ihren Internetbrowser übermittelten Daten während der
                laufenden Verbindung zur Kommunikation zwischen Ihrem Internetbrowser und dem Webserver aufgezeichnet
                und nur temporär gespeichert.
              </LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Hochgeladene Fotos:</LegalText>
              <LegalText>
                Fotos, die Sie zur Dokumentation von Mängeln hochladen, werden zusammen mit Ihrer Meldung gespeichert
                und zur Visualisierung der gemeldeten Mängel verwendet.
              </LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Geodaten:</LegalText>
              <LegalText>
                Die eingegebenen Ortsdaten zur Meldung werden ausschließlich für die Meldungsbearbeitung genutzt. Wenn
                gewünscht, kann dafür auch der Gerätestandort genutzt werden.
              </LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Webtracking:</LegalText>
              <LegalText>
                Zur bedarfsgerechten Gestaltung und laufenden Verbesserung des Internetangebots kann das
                Web-Analyse-Werkzeug „Umami“ eingesetzt werden. Für die Analyse werden Daten wie Besuchszeit, besuchte
                Seiten, Verweildauer und regionale Herkunft in anonymisierter Form erhoben und verarbeitet. Es werden
                keine Cookies verwendet, und die Identifizierung der Besucher anhand der erhobenen Daten ist
                ausgeschlossen. Die dabei erfassten Daten werden ausschließlich in aggregierter und anonymisierter Form
                ausgewertet. Es erfolgt keine Erstellung von Nutzerprofilen und keine Verknüpfung mit anderen
                personenbezogenen Daten.
              </LegalText>
              <LegalText>Sie können hier dem Webtracking widersprechen:</LegalText>
              <DoNotTrackCheckbox />
            </li>
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Werden die personenbezogenen Daten weitergegeben?</LegalText>

          <LegalText>
            Ihre personenbezogenen Daten werden nur an die jeweils zuständige Behörde weitergeleitet, sofern dies zur
            Bearbeitung Ihrer Meldung erforderlich ist. Eine Weitergabe an Dritte erfolgt nicht, es sei denn, es besteht
            eine gesetzliche Verpflichtung oder Sie haben ausdrücklich eingewilligt. Die Weiterleitung erfolgt im Rahmen
            der gesetzlichen Zuständigkeiten der empfangenden Stellen. Soweit Dienstleister mit der technischen
            Umsetzung betraut sind, erfolgt die Verarbeitung im Rahmen einer Auftragsverarbeitung gemäß Art. 28 DS-GVO.
          </LegalText>
        </li>

        <li>
          <LegalText fontWeight="bold">Rechte der betroffenen Personen</LegalText>

          <LegalText>
            Zur Wahrnehmung Ihrer Rechte können Sie sich an die unter Ziff. 1 oder 2 genannte Kontaktadresse wenden.
          </LegalText>
          <LegalText>Nach der Datenschutz-Grundverordnung haben Sie folgende Rechte:</LegalText>
          <LegalText component="ul">
            <li>
              <LegalText fontWeight="bold">Auskunftsrecht (Art. 15 DS-GVO):</LegalText>
              <LegalText>Sie können Informationen über Ihre gespeicherten Daten anfordern.</LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Recht auf Berichtigung (Art. 16 DS-GVO):</LegalText>
              <LegalText>Falls Ihre Daten nicht korrekt sind, können Sie eine Korrektur verlangen.</LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Recht auf Löschung (Art. 17 DS-GVO):</LegalText>
              <LegalText>Sie können die Löschung Ihrer personenbezogenen Daten beantragen.</LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Recht auf Einschränkung der Verarbeitung (Art. 18 DS-GVO):</LegalText>
              <LegalText>Sie können unter bestimmten Bedingungen die Verarbeitung einschränken lassen.</LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Recht auf Datenübertragbarkeit (Art. 20 DS-GVO):</LegalText>
              <LegalText>
                Falls die Verarbeitung auf Ihrer Einwilligung beruht, können Sie Ihre Daten in maschinenlesbarer Form
                erhalten.
              </LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Widerspruchsrecht (Art. 21 DS-GVO):</LegalText>
              <LegalText>Sie können der Verarbeitung Ihrer Daten widersprechen.</LegalText>
            </li>
            <li>
              <LegalText fontWeight="bold">Widerrufsrecht (Art. 7 Abs. 3 DS-GVO):</LegalText>
              <LegalText>
                Falls Sie eine Einwilligung zur Verarbeitung erteilt haben, können Sie diese jederzeit widerrufen.
              </LegalText>
            </li>

            <li>
              <LegalText fontWeight="bold">Beschwerderecht (Art. 77 DS-GVO):</LegalText>
              <LegalText>
                Wenn Sie Fragen oder Bedenken im Hinblick auf die Verarbeitung Ihrer personenbezogenen Daten haben,
                können Sie das jeweilig verantwortliche Regierungspräsidium postalisch oder per E-Mail kontaktieren.
                Darüber hinaus können Sie Ihr Anliegen auch der bzw. dem behördlichen Datenschutzbeauftragten zukommen
                lassen. Die entsprechenden Adressen finden Sie unter Ziff. 2.
              </LegalText>
              <LegalText>
                Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren. Zuständig ist der Landesbeauftragte
                für Datenschutz und Informationsfreiheit Baden-Württemberg (LfDI BW), Königstraße 10, 70173 Stuttgart.
              </LegalText>
            </li>
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Änderungen dieser Datenschutzerklärung</LegalText>
          <LegalText>
            Diese Datenschutzerklärung kann bei Bedarf aktualisiert werden. Die aktuelle Version ist jederzeit auf der
            Webseite des RadMELDER abrufbar.
          </LegalText>
        </li>
      </LegalText>
    </Fragment>
  );
}
